import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Receipt, Plus, RefreshCw, Upload, FilterIcon,
} from 'lucide-react';
import { admin, getErrorMessage } from '../../utils/api';
import EvidenceViewer from '../shared/EvidenceViewer';

const REASONS = [
  { value: 'spoiled',                     group: 'Retailer' },
  { value: 'expired',                     group: 'Retailer' },
  { value: 'damaged',                     group: 'Retailer' },
  { value: 'mislabeled',                  group: 'Retailer' },
  { value: 'recall',                      group: 'Retailer' },
  { value: 'missing_item_retailer_fault', group: 'Retailer' },
  { value: 'missing_item_courier_fault',  group: 'Courier' },
  { value: 'delivery_damage',             group: 'Courier' },
  { value: 'entire_order_undelivered',    group: 'Courier' },
  { value: 'customer_changed_mind',       group: 'DealShaq' },
  { value: 'fraudulent_claim',            group: 'DealShaq' },
  { value: 'other',                       group: 'DealShaq' },
];

const _fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; }
};

const _faultBadge = (fault) => {
  const map = {
    retailer: 'bg-orange-100 text-orange-800',
    courier: 'bg-blue-100 text-blue-800',
    dealshaq: 'bg-slate-100 text-slate-800',
  };
  return <Badge className={map[fault] || 'bg-gray-100 text-gray-800'}>{fault}</Badge>;
};

const _statusBadge = (status) => {
  const map = {
    applied: 'bg-amber-100 text-amber-800',
    disputed: 'bg-purple-100 text-purple-800',
    resolved: 'bg-emerald-100 text-emerald-800',
    reversed: 'bg-emerald-100 text-emerald-800',
  };
  return <Badge className={map[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
};

function CreateRefundModal({ open, onOpenChange, onCreated }) {
  const [orderId, setOrderId] = useState('');
  const [itemId, setItemId] = useState('');
  const [reason, setReason] = useState('spoiled');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const _reset = () => {
    setOrderId(''); setItemId(''); setReason('spoiled'); setNotes(''); setFiles([]);
  };

  const _close = () => { _reset(); onOpenChange(false); };

  const _submit = async () => {
    if (!orderId.trim() || !itemId.trim()) {
      toast.error('Order ID and Item ID are required.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await admin.createRefund({
        order_id: orderId.trim(),
        item_id: itemId.trim(),
        reason,
        admin_notes: notes.trim() || undefined,
        evidence_urls: [],
      });
      const adjId = data?.adjustment?.id;
      if (files.length && adjId) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        await admin.uploadRefundEvidence(adjId, fd);
      }
      toast.success('Refund adjustment created.');
      onCreated && onCreated();
      _close();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create refund'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : _close())}>
      <DialogContent className="max-w-lg" data-testid="admin-create-refund-modal">
        <DialogHeader>
          <DialogTitle>Create Refund Adjustment</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Order ID *</label>
            <Input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="uuid…"
              data-testid="refund-order-id"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Item ID *</label>
            <Input
              value={itemId}
              onChange={(e) => setItemId(e.target.value)}
              placeholder="uuid…"
              data-testid="refund-item-id"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Reason *</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="refund-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.group} · {r.value.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Internal notes</label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — admin-only context"
              data-testid="refund-notes"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              Evidence (optional — jpg/png/pdf, ≤10 MB each)
            </label>
            <input
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
              className="text-xs"
              data-testid="refund-files"
            />
            {files.length > 0 && (
              <p className="text-xs text-gray-600 mt-1">{files.length} file(s) staged</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={_close} disabled={submitting}>Cancel</Button>
          <Button
            onClick={_submit}
            disabled={submitting}
            data-testid="refund-submit-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? 'Creating…' : 'Create Adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRefundAdjustments({ user, onLogout }) {
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retailerFilter, setRetailerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [buildRetailer, setBuildRetailer] = useState('');
  const [building, setBuilding] = useState(false);

  const _load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (retailerFilter.trim()) params.retailer_id = retailerFilter.trim();
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const { data } = await admin.listRefunds(params);
      setAdjustments(data?.adjustments || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load refunds'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { _load(); }, []); // eslint-disable-line

  const _buildSettlement = async () => {
    if (!buildRetailer.trim()) {
      toast.error('Enter a retailer_id to build a rollup.');
      return;
    }
    const now = new Date();
    setBuilding(true);
    try {
      const { data } = await admin.buildSettlement({
        retailer_id: buildRetailer.trim(),
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      });
      toast.success(
        `Rollup built — Net $${Number(data?.settlement?.net_payable || 0).toFixed(2)}`,
      );
      _load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to build settlement'));
    } finally {
      setBuilding(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-refunds-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="w-6 h-6 text-emerald-600" />
              Refund Adjustments
            </h1>
            <p className="text-sm text-gray-600">
              Create refund adjustments per §2 Defect Liability. Retailer-fault reasons
              withhold wholesale value; courier/DS-absorb reasons do not.
            </p>
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="admin-open-create-refund"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Adjustment
          </Button>
        </div>

        {/* Monthly rollup builder */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Build Monthly Rollup</CardTitle>
            <CardDescription>
              Idempotent per (retailer, year, month). Rolls unlinked adjustments into a statement and opens the 7-day dispute window.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-gray-600 mb-1 block">Retailer ID</label>
              <Input
                value={buildRetailer}
                onChange={(e) => setBuildRetailer(e.target.value)}
                placeholder="retailer uuid…"
                data-testid="build-retailer-id"
              />
            </div>
            <Button
              onClick={_buildSettlement}
              disabled={building}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="build-settlement-btn"
            >
              {building ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Build Current Month
            </Button>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FilterIcon className="w-4 h-4" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Retailer ID</label>
              <Input
                value={retailerFilter}
                onChange={(e) => setRetailerFilter(e.target.value)}
                placeholder="filter by retailer…"
                data-testid="filter-retailer-id"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 mb-1 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="applied">Applied</SelectItem>
                  <SelectItem value="disputed">Disputed</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={_load} data-testid="filter-apply-btn">
              Apply
            </Button>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-gray-500">Loading…</p>
            ) : adjustments.length === 0 ? (
              <p className="p-6 text-sm text-gray-500" data-testid="admin-refunds-empty">
                No adjustments yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>Order</TableHead>
                      <TableHead>Retailer</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Fault</TableHead>
                      <TableHead className="text-right">Refund</TableHead>
                      <TableHead className="text-right">Withheld</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Evidence</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map((a) => (
                      <TableRow key={a.id} data-testid={`admin-refund-row-${a.id}`}>
                        <TableCell className="font-mono text-xs">{a.id.slice(0, 8)}</TableCell>
                        <TableCell className="font-mono text-xs">{a.order_id?.slice(0, 8)}</TableCell>
                        <TableCell className="font-mono text-xs">{a.retailer_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-xs capitalize">
                          {a.reason.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>{_faultBadge(a.fault)}</TableCell>
                        <TableCell className="text-right">
                          ${Number(a.refund_amount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${Number(a.withheld_amount || 0).toFixed(2)}
                        </TableCell>
                        <TableCell>{_statusBadge(a.status)}</TableCell>
                        <TableCell>
                          {(a.evidence_urls?.length ?? 0) > 0 ? (
                            <EvidenceViewer urls={a.evidence_urls} title="" />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{_fmtDate(a.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <CreateRefundModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={_load}
        />
      </div>
    </AdminLayout>
  );
}
